/**
 * interior_debug.ts — THE PLANNER'S DEBUG OVERLAY in the live interior (the interiors programme, slice C, 2026-10-01).
 *
 * `__interior.debug(true)` draws slice B's `interiorDebugView` over the room the player stands in: the reserved
 * regions (door corridor, stair zone, landing, the console's ground, the lift shaft), the zones (by access), every
 * piece's oriented footprint (by tier), the anchors (the standing point, a tie to the thing, the facing), the route
 * tree from the floor's entry, the units — and a text panel: the digest and recipe, the status and rung, the reasons,
 * and WHY EACH CANDIDATE LOST. Dev-gated (`localhost` or `localStorage atlas:debug = '1'`).
 *
 * Geometry: flat ribbons with real normals, ONE mesh per colour (THE MISSING-ATTRIBUTE LAW — a point-built line has no
 * normal and an MRT pass reads one; a debug view must not break the pipeline it inspects). Unlit, fog-free, opaque.
 */
import * as THREE from 'three/webgpu';
import { interiorDebugView, type DebugView, type InteriorDescriptor, type PlanReport, type Rect } from '../../lib/interior_descriptor.js';
import { disposeLater } from '../../lib/gpu_dispose.js';

export function interiorDebugAllowed(): boolean {
  const host = (typeof location !== 'undefined' ? location.hostname : '') || '';
  const local = host === 'localhost' || host === '127.0.0.1' || host === '[::1]' || host === '';
  let optIn = false;
  try { optIn = typeof localStorage !== 'undefined' && localStorage.getItem('atlas:debug') === '1'; } catch { /* storage blocked */ }
  return local || optIn;
}

/** World colours of the overlay (data, like every debug view's): what each ribbon means. */
const COLORS = {
  reserved: 0xd94848,   // the door corridor, the stair zone, the landing, the console, the shell
  lift: 0x9a5cf0,       // the lift candidate's shaft and apron
  zonePublic: 0x3fbf7f, zoneOwner: 0xe0b040, zoneStaff: 0x4f9fe8, zoneGuarded: 0xd070c0,
  primary: 0xffffff, secondary: 0xb8c4d0, detail: 0x7a8794, owned: 0xffa040,
  anchor: 0x40e0e0, seat: 0xfff070, route: 0x3070ff, unit: 0xff70a0,
} as const;
type ColorKey = keyof typeof COLORS;

class Ribbons {
  private readonly pos = new Map<ColorKey, number[]>();
  /** A flat ribbon from (x0, z0) to (x1, z1), `w` wide, at height y (building-local). */
  seg(c: ColorKey, x0: number, z0: number, x1: number, z1: number, y: number, w = 0.05): void {
    const dx = x1 - x0, dz = z1 - z0, len = Math.hypot(dx, dz);
    if (!(len > 1e-4)) return;
    const nx = (-dz / len) * (w / 2), nz = (dx / len) * (w / 2);
    let arr = this.pos.get(c);
    if (!arr) { arr = []; this.pos.set(c, arr); }
    arr.push(x0 - nx, y, z0 - nz, x1 - nx, y, z1 - nz, x1 + nx, y, z1 + nz, x0 - nx, y, z0 - nz, x1 + nx, y, z1 + nz, x0 + nx, y, z0 + nz);
  }
  rect(c: ColorKey, r: Rect, y: number, w = 0.05): void {
    this.seg(c, r.x0, r.z0, r.x1, r.z0, y, w); this.seg(c, r.x1, r.z0, r.x1, r.z1, y, w);
    this.seg(c, r.x1, r.z1, r.x0, r.z1, y, w); this.seg(c, r.x0, r.z1, r.x0, r.z0, y, w);
  }
  poly(c: ColorKey, pts: ReadonlyArray<readonly [number, number]>, y: number, closed: boolean, w = 0.05): void {
    for (let i = 0; i + 1 < pts.length; i++) this.seg(c, pts[i][0], pts[i][1], pts[i + 1][0], pts[i + 1][1], y, w);
    if (closed && pts.length > 2) this.seg(c, pts[pts.length - 1][0], pts[pts.length - 1][1], pts[0][0], pts[0][1], y, w);
  }
  dot(c: ColorKey, x: number, z: number, y: number, r = 0.12): void {
    this.seg(c, x - r, z, x + r, z, y, r * 2);
  }
  build(): THREE.Mesh[] {
    const out: THREE.Mesh[] = [];
    for (const [c, arr] of this.pos) {
      const geo = new THREE.BufferGeometry();
      geo.setAttribute('position', new THREE.Float32BufferAttribute(arr, 3));
      const n = new Float32Array(arr.length);
      for (let i = 1; i < n.length; i += 3) n[i] = 1;
      geo.setAttribute('normal', new THREE.BufferAttribute(n, 3));
      geo.setAttribute('uv', new THREE.BufferAttribute(new Float32Array((arr.length / 3) * 2), 2));
      const mat = new THREE.MeshBasicMaterial({ color: COLORS[c], side: THREE.DoubleSide, toneMapped: false, fog: false });
      const mesh = new THREE.Mesh(geo, mat);
      mesh.name = `interior-debug:${c}`;
      mesh.frustumCulled = false;
      out.push(mesh);
    }
    return out;
  }
}

export interface InteriorDebugHandle { group: THREE.Group; panel: HTMLElement; view: DebugView; counts: Record<string, number>; dispose(): void }

/** Build the overlay for a descriptor at a building's pose. The caller adds / removes `group`; `dispose` frees it. */
export function buildInteriorDebug(
  d: InteriorDescriptor, report: PlanReport | null, shell: { hw: number; hd: number; floors: number },
  pose: { x: number; z: number; yaw: number; groundY: number }, extra: { title: string; lines: string[] },
): InteriorDebugHandle {
  const view = interiorDebugView(d, report, shell);
  const rb = new Ribbons();
  const counts = { reserved: 0, zones: 0, pieces: 0, anchors: 0, routes: 0, units: 0, lift: 0 };
  for (const f of view.floors) {
    const y = f.floorY + 0.03;
    for (const r of f.reserved) { rb.rect(r.kind === 'lift' ? 'lift' : 'reserved', r.rect, y, 0.04); counts.reserved++; }
    for (const z of f.zones) {
      const c: ColorKey = z.access === 'owner' ? 'zoneOwner' : z.access === 'employee' ? 'zoneStaff' : z.access === 'guarded' ? 'zoneGuarded' : 'zonePublic';
      for (const r of z.rects) rb.rect(c, { x0: r.x0 + 0.06, z0: r.z0 + 0.06, x1: r.x1 - 0.06, z1: r.z1 - 0.06 }, y + 0.01, 0.07);
      counts.zones++;
    }
    for (const o of f.occupied) { rb.poly(o.owned ? 'owned' : o.tier === 'primary' ? 'primary' : o.tier === 'secondary' ? 'secondary' : 'detail', o.corners, y + 0.02, true, 0.04); counts.pieces++; }
    for (const r of f.routes) { rb.poly('route', r.points, y + 0.015, false, 0.035); counts.routes++; }
    for (const s of f.sockets) {
      rb.dot(s.seat ? 'seat' : 'anchor', s.ax, s.az, y + 0.03, 0.1);
      rb.seg('anchor', s.ax, s.az, s.x, s.z, y + 0.025, 0.02);
      rb.seg(s.seat ? 'seat' : 'anchor', s.ax, s.az, s.ax + Math.sin(s.facing) * 0.35, s.az + Math.cos(s.facing) * 0.35, y + 0.03, 0.05);
      counts.anchors++;
    }
    for (const u of f.units) { rb.rect('unit', u.rect, y + 0.035, 0.08); counts.units++; }
  }
  if (view.lift) { for (const c of view.lift.calls) { rb.rect('lift', view.lift.rect, (c.floor === 0 ? 0.08 : c.floor * 3.4) + 0.04, 0.08); counts.lift++; } }
  const group = new THREE.Group();
  group.name = 'interior-debug';
  group.position.set(pose.x, pose.groundY, pose.z); // a scene child is ABSOLUTE (the frame law)
  group.rotation.y = pose.yaw;
  const meshes = rb.build();
  for (const m of meshes) group.add(m);

  const panel = document.createElement('pre');
  panel.id = 'interior-debug-panel';
  panel.style.cssText = 'position:fixed;left:10px;top:64px;z-index:60;max-width:560px;max-height:70vh;overflow:auto;margin:0;padding:8px 10px;'
    + 'border-radius:var(--ax-radius);border:1px solid var(--ax-line2);background:color-mix(in srgb, var(--ax-bg) 88%, transparent);'
    + 'color:var(--ax-text);font:11px/1.35 var(--ax-mono);white-space:pre-wrap;pointer-events:auto';
  const lines: string[] = [
    extra.title,
    `digest  ${view.digest}`,
    `recipe  ${view.recipe}`,
    `status  ${view.status} · rung ${view.rung} · ${view.floors.length} floor${view.floors.length === 1 ? '' : 's'}`,
    ...extra.lines,
    `drawn   ${counts.pieces} pieces · ${counts.zones} zones · ${counts.anchors} anchors · ${counts.routes} routes · ${counts.reserved} reserved · ${counts.units} units${view.lift ? ' · lift candidate' : ''}`,
  ];
  if (view.reasons.length) lines.push('', 'reasons', ...view.reasons.map((r) => `  ${r}`));
  if (view.hard.length) lines.push('', `HARD (${view.hard.length})`, ...view.hard.slice(0, 24).map((r) => `  ${r}`));
  if (view.candidates.length) {
    lines.push('', 'candidates (why each lost)');
    for (const c of view.candidates.slice(0, 40)) lines.push(`  ${c.status === 'valid' ? '✓' : '✗'} ${c.id}${c.status === 'valid' ? `  score ${c.score}` : ''}${c.reasons.length ? `  — ${c.reasons.slice(0, 4).join('; ')}` : ''}`);
  }
  lines.push('', 'legend  red reserved · purple lift · green/gold/blue/pink zones (public/owner/staff/guarded) · white/grey pieces · cyan anchors · yellow seats · blue routes');
  panel.textContent = lines.join('\n');
  document.body.appendChild(panel);

  return {
    group, panel, view, counts,
    dispose: (): void => {
      panel.remove();
      group.parent?.remove(group);
      for (const m of meshes) { disposeLater(m.geometry); disposeLater(m.material as THREE.Material); } // the warp-teardown law: never inside a submit window
    },
  };
}
